import {useEffect} from 'react';
import authService from '../../../services/AuthService';

type VerboxApi = ((method: string, ...args: any[]) => void) & {q?: unknown[]};

declare global {
  interface Window {
    Verbox?: VerboxApi;
    supportAPIMethod?: string;
    __verboxLoaded?: boolean;
  }
}

const widgetId = process.env.REACT_APP_VERBOX_WIDGET_ID ?? '24d89441568cc3ddde97d055e12507ab';
export const isVerboxEnabled = process.env.REACT_APP_VERBOX_ENABLED !== 'false' && Boolean(widgetId);
export const hiddenVerboxClassName = 'verbox-widget-collapsed';
const ownerKey = 'bigclock.verbox.owner';
let loadingPromise: Promise<void> | null = null;

const readOwner = (): string | null => {
  try {
    const user = JSON.parse(localStorage.getItem('user') || 'null');
    return user?.token && (user.login || user.name)
      ? String(user.login || user.name)
      : null;
  } catch {
    return null;
  }
};

export const hasVerboxOwner = () => Boolean(readOwner());

export const prepareVerboxOwner = async () => {
  const owner = readOwner();
  if (!owner) throw new Error('Чат доступен после входа в приложение.');
  const previousOwner = localStorage.getItem(ownerKey);
  if (previousOwner === owner) return;

  // A missing marker is the first launch of this integration. Associate the
  // already loaded browser session with the current authenticated account.
  if (!previousOwner) {
    localStorage.setItem(ownerKey, owner);
    return;
  }

  // The widget may have restored a browser-local conversation belonging to a
  // different application account. Clear it while the iframe is still hidden.
  localStorage.setItem(ownerKey, owner);
  window.Verbox?.('reload');
};

export function loadVerboxWidget(): Promise<void> {
  if (!isVerboxEnabled) return Promise.reject(new Error('Чат отключён.'));
  if (window.__verboxLoaded) return Promise.resolve();
  if (loadingPromise) return loadingPromise;

  document.body.classList.add(hiddenVerboxClassName);
  loadingPromise = new Promise<void>((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>('script[data-verbox-widget]');
    if (existing) return;

    const script = document.createElement('script');
    // Verbox expects a command queue to exist before support.js is loaded.
    const queued = ((...args: any[]) => {
      (queued as VerboxApi).q = (queued as VerboxApi).q || [];
      (queued as VerboxApi).q!.push(args);
    }) as VerboxApi;
    window.Verbox = queued;
    window.supportAPIMethod = 'Verbox';
    script.src = 'https://admin.verbox.ru/support/support.js?h=' + encodeURIComponent(widgetId);
    script.async = true;
    script.dataset.verboxWidget = widgetId;
    script.onload = () => { window.__verboxLoaded = true; loadingPromise = null; resolve(); };
    script.onerror = () => {
      if (script.src.includes('admin.verbox.ru')) {
        script.src = 'https://static.site-chat.me/support/support.int.js?h=' + encodeURIComponent(widgetId);
        return;
      }
      loadingPromise = null;
      script.remove();
      reject(new Error('Не удалось загрузить чат. Проверьте домен сайта в кабинете Verbox.'));
    };
    document.head.appendChild(script);
  });

  return loadingPromise;
}

const hideAndCloseWidget = () => {
  document.body.classList.add(hiddenVerboxClassName);
  window.Verbox?.('closeSupport');
};

// This component manages cleanup only. The Verbox script is loaded lazily after
// an explicit click on the maintenance button.
export default function VerboxChat({pathname = window.location.pathname}: {pathname?: string}) {
  useEffect(() => {
    if (!isVerboxEnabled) return;

    let activeOwner = readOwner();
    document.body.classList.add(hiddenVerboxClassName);

    const sync = () => {
      const owner = readOwner();
      if (owner === activeOwner) return;

      hideAndCloseWidget();
      window.Verbox?.('reload');
      localStorage.removeItem(ownerKey);
      activeOwner = owner;
    };

    const subscription = authService.subscribe(sync);
    const onStorage = (event: StorageEvent) => {
      if (event.key === 'user' || event.key === null) sync();
    };
    window.addEventListener('storage', onStorage);

    return () => {
      authService.unsubscribe(subscription);
      window.removeEventListener('storage', onStorage);
      hideAndCloseWidget();
      window.Verbox?.('closeSupport');
    };
  }, []);

  useEffect(() => {
    if (isVerboxEnabled) hideAndCloseWidget();
  }, [pathname]);

  return null;
}
